import React, { useState, useEffect } from "react";
import {
  Tooltip,
  Button,
  Typography,
  Box,
  Fade,
  Paper,
  CircularProgress,
} from "@material-ui/core";
import {
  Update as UpdateIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { i18n } from "../../translate/i18n";

const packageVersion = require("../../../package.json").version;

const useStyles = makeStyles((theme) => ({
  banner: {
    position: "fixed",
    top: 64,
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: theme.zIndex.drawer + 2,
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
    backgroundColor: theme.palette.info.main,
    color: "white",
    padding: theme.spacing(1.5, 3),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    maxWidth: "600px",
    minWidth: "400px",
    width: "90%",
    [theme.breakpoints.down("sm")]: {
      width: "95%",
      minWidth: "300px",
      padding: theme.spacing(1, 2),
    },
  },
  pulseIcon: {
    animation: "$pulse 2s infinite",
    color: theme.palette.error.main,
  },
  updateButton: {
    backgroundColor: theme.palette.success.main,
    color: "white",
    fontWeight: "bold",
    "&:hover": {
      backgroundColor: theme.palette.success.dark,
    },
  },
  bannerButton: {
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    color: "white",
    border: "1px solid rgba(255, 255, 255, 0.3)",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.3)",
    },
  },
  iconContainer: {
    display: "flex",
    alignItems: "center",
    marginRight: theme.spacing(1),
  },
  "@keyframes pulse": {
    "0%": { transform: "scale(1)", opacity: 1 },
    "50%": { transform: "scale(1.1)", opacity: 0.8 },
    "100%": { transform: "scale(1)", opacity: 1 },
  },
}));

const VersionControl = () => {
  const classes = useStyles();
  // Navegador sem versão gravada é navegador novo: não há cache de outra
  // versão para limpar, então grava a atual em silêncio. Sem essa gravação o
  // localStorage ficaria vazio para sempre (só handleUpdate escreve) e
  // nenhuma atualização futura chegaria a disparar o banner. Antes daqui
  // havia um "4.7.7" fixo, herdado do fork e órfão desde a renumeração.
  const [storedVersion] = useState(() => {
    try {
      const salvo = window.localStorage.getItem("version");
      if (salvo) return salvo;
      window.localStorage.setItem("version", packageVersion);
      return packageVersion;
    } catch (err) {
      // localStorage indisponível (modo privado, quota): sem banner
      return packageVersion;
    }
  });
  const [showBanner, setShowBanner] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const hasNewVersion = storedVersion !== packageVersion;

  useEffect(() => {
    if (hasNewVersion) {
      const timer = setTimeout(() => setShowBanner(true), 2000);
      return () => clearTimeout(timer);
    }
  }, [hasNewVersion]);

  // Atualizar é uma ação puramente LOCAL: marca a versão vista neste
  // navegador, descarta os caches dele e recarrega. Antes daqui saía também um
  // POST /version, que gravava a versão da plataforma inteira a partir do
  // clique de um usuário qualquer — o número oficial passou a subir por
  // migração no deploy, e a rota agora exige super admin.
  const handleUpdate = async () => {
    setIsUpdating(true);

    try {
      window.localStorage.setItem("version", packageVersion);

      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }

      setTimeout(() => window.location.reload(), 1000);
    } catch (error) {
      console.error("Erro na atualização:", error);
      setIsUpdating(false);
    }
  };

  if (!hasNewVersion) {
    return null;
  }

  return (
    <>
      {/* Banner de Atualização Centralizado */}
      <Fade in={showBanner && !isUpdating}>
        <Paper className={classes.banner} elevation={4}>
          <Box display="flex" alignItems="center" flex={1}>
            <div className={classes.iconContainer}>
              <InfoIcon />
            </div>
            {/* Sem o número da versão: o usuário final não precisa dele, e a
                ação ("atualizar") é a mesma independente de qual versão é. */}
            <Typography variant="body2">
              <strong>{i18n.t("versionControl.title")}</strong>
              {` ${i18n.t("versionControl.subtitle")}`}
            </Typography>
          </Box>
          <Box display="flex" gap={1} alignItems="center" ml={2}>
            {isUpdating && <CircularProgress size={20} style={{ color: "white" }} />}
            <Button
              size="small"
              onClick={handleUpdate}
              disabled={isUpdating}
              startIcon={!isUpdating && <RefreshIcon />}
              className={classes.updateButton}
            >
              {isUpdating
                ? i18n.t("versionControl.updating")
                : i18n.t("versionControl.update")}
            </Button>
          </Box>
        </Paper>
      </Fade>
    </>
  );
};

export default VersionControl;